import { useIsAuthenticated } from '@azure/msal-react';
import { refreshTokenHandler } from '@brightdrop/auth-client';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useEffect, useRef, useState } from 'react';

import { API_VERSION_DEFAULTS } from '~/common/apis/api.constants';
import { PUB_SUB_FLAG } from '~/common/models/featureFlags.model';

import type { TokenResponse } from './pubSub.model';

const MINUTE_55 = 1000 * 60 * 55;

const useFetchUrl = () => {
  const [url, setURL] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { getFlag } = useFeatureFlags();
  const isPubSubEnabled = getFlag(PUB_SUB_FLAG);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    if (!isAuthenticated || !isPubSubEnabled) {
      return;
    }

    const getURL = async () => {
      try {
        const results = await refreshTokenHandler();
        if (!results?.accessToken) {
          return;
        }

        // token valid for 1 hour
        const req = await fetch(
          `${globalThis.appConfig.apiBaseUrl}/communications/${API_VERSION_DEFAULTS.pubSubAPI}/pubSubToken`,
          {
            method: 'post',
            headers: {
              'Content-Type': 'application/json',
              authorization: `Bearer ${results?.accessToken}`,
            },
          }
        );
        const res = (await req.json()) as TokenResponse;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // url only good for one hour, refresh url every 55 minutes
        timeoutRef.current = setTimeout(() => {
          getURL();
        }, MINUTE_55);

        setURL(res?.result?.url ?? '');
      } catch (e) {
        console.error('Error getting URL:', e);
      }
    };

    getURL();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, isPubSubEnabled]);

  return { url };
};

export { useFetchUrl };
